import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/core/card";
import { Content } from "@/components/layouts/content";
import { LeadForm } from "@/components/leads/lead-form";
import {
	leadActivitiesQueryOptions,
	leadNotesQueryOptions,
	leadQueryOptions,
	leadStatusesQueryOptions,
	useLeadNoteCreateMutation,
	useLeadNoteDeleteMutation,
	useLeadUpdateMutation,
} from "@/features/dashboard/api/queries/lead-query";
import leadService from "@/features/dashboard/api/services/lead-service";
import type { ActivityLog, Lead, LeadNote } from "@/features/dashboard/types/lead";
import type { CreateLeadData } from "@/features/dashboard/types/lead";
import { formatNumber } from "@/utils/format-number";
import * as Dialog from "@radix-ui/react-dialog";
import {
	IconAlertTriangle,
	IconArrowLeft,
	IconCalendar,
	IconCalendarEvent,
	IconCurrencyPeso,
	IconDownload,
	IconEdit,
	IconFile,
	IconMail,
	IconNote,
	IconPaperclip,
	IconPhone,
	IconPlus,
	IconRefresh,
	IconTrash,
	IconUser,
	IconX,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { useRef, useState } from "react";
import { toast } from "sonner";

const BADGE_COLOR_MAP: Record<string, "yellow-light" | "green-light" | "red-light" | "blue-light" | "purple-light" | "gray-light"> = {
	green: "green-light",
	yellow: "yellow-light",
	red: "red-light",
	blue: "blue-light",
	purple: "purple-light",
	gray: "gray-light",
	orange: "yellow-light",
};

export const Route = createFileRoute("/_dashboard/leads_/$leadId")({
	component: LeadDetailPage,
});

function LeadDetailPage() {
	const { leadId } = Route.useParams();
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);

	const leadQuery = useQuery(leadQueryOptions(leadId));
	const activitiesQuery = useQuery(leadActivitiesQueryOptions(leadId));
	const notesQuery = useQuery(leadNotesQueryOptions(leadId));
	const statusesQuery = useQuery(leadStatusesQueryOptions());
	const statuses = statusesQuery.data || [];
	const statusLabels = Object.fromEntries(statuses.map((s) => [s.slug, s.name]));
	const statusBadgeColors = Object.fromEntries(statuses.map((s) => [s.slug, BADGE_COLOR_MAP[s.color] || "gray-light"]));

	const lead = leadQuery.data;
	const activities = activitiesQuery.data?.data || [];

	if (leadQuery.isLoading) {
		return (
			<Content pageName="Lead Details">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
						<p className="mt-2 text-sm text-gray-600">Loading lead details...</p>
					</div>
				</div>
			</Content>
		);
	}

	if (!lead) {
		return (
			<Content pageName="Lead Details">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<p className="text-lg text-gray-600">Lead not found</p>
					</div>
				</div>
			</Content>
		);
	}

	const formatCurrency = (value: number) =>
		new Intl.NumberFormat("en-PH", {
			style: "currency",
			currency: "PHP",
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(value);

	const formatDate = (value?: string | null) => {
		if (!value) return "-";
		return new Date(value).toLocaleDateString("en-PH", {
			year: "numeric",
			month: "long",
			day: "numeric",
		});
	};

	return (
		<Content pageName={`Lead: ${lead.reference_number}`}>
			<div className="p-6 space-y-6">
				{/* Header */}
				<div className="flex items-start justify-between">
					<div className="flex items-center gap-3">
						<Link to="/leads" className="text-gray-500 hover:text-gray-700 transition-colors">
							<IconArrowLeft className="w-5 h-5" />
						</Link>
						<div>
							<div className="flex items-center gap-3">
								<h1 className="text-2xl font-bold text-gray-900">{lead.reference_number}</h1>
								<Badge size="xs" color={statusBadgeColors[lead.status] || "gray"}>
									{statusLabels[lead.status] || lead.status}
								</Badge>
							</div>
							<p className="text-sm text-gray-500 mt-1">{lead.project_description}</p>
						</div>
					</div>
					<Button onClick={() => setIsEditModalOpen(true)}>
						<IconEdit size={18} stroke={2} />
						<span>Edit</span>
					</Button>
				</div>

				{/* Info Cards Row */}
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
					<InfoCard icon={<IconUser className="w-5 h-5" />} label="Client" value={lead.client_name} />
					<InfoCard icon={<IconCurrencyPeso className="w-5 h-5" />} label="Amount" value={formatCurrency(lead.amount)} />
					<InfoCard icon={<IconCalendar className="w-5 h-5" />} label="Proposal Date" value={formatDate(lead.proposal_date)} />
					<InfoCard
						icon={<IconAlertTriangle className="w-5 h-5" />}
						label="Priority"
						value={lead.priority ? lead.priority.charAt(0).toUpperCase() + lead.priority.slice(1) : "Not set"}
					/>
					<InfoCard
						icon={<IconCalendarEvent className="w-5 h-5" />}
						label="Next Follow-up"
						value={lead.next_follow_up_date ? formatDate(lead.next_follow_up_date) : "Not set"}
					/>
				</div>

				{/* 3-column Card Grid */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					{/* Lead Information */}
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">Lead Information</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-4">
								<DetailItem label="Reference Number" value={lead.reference_number} />
								<DetailItem label="Project Description" value={lead.project_description} />
								<DetailItem label="Client Name" value={lead.client_name} />
								<DetailItem label="Amount" value={formatCurrency(lead.amount)} />
								<DetailItem label="Category" value={lead.category} />
								<DetailItem label="Prepared By" value={lead.prepared_by_user?.name || "-"} />
							</div>
						</CardContent>
					</Card>

					{/* Contact Details */}
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">Contact Details</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-4">
								<DetailItem label="Contact Person" value={lead.contact_person || "-"} />
								<DetailItem label="Contact Number" value={lead.contact_number || "-"} />
								<DetailItem label="Email" value={lead.email || "-"} />
								<DetailItem label="Client Address" value={lead.client_address || "-"} />
							</div>
						</CardContent>
					</Card>

					{/* Dates & Status */}
					<Card>
						<CardHeader>
							<CardTitle className="text-lg">Dates & Status</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-4">
								<DetailItem label="Proposal Date" value={formatDate(lead.proposal_date)} />
								<DetailItem label="Request Date" value={formatDate(lead.request_date)} />
								<DetailItem label="Response Date" value={formatDate(lead.response_date)} />
								<div>
									<p className="text-xs text-gray-500 font-medium">Status</p>
									<div className="mt-1">
										<Badge size="xs" color={statusBadgeColors[lead.status] || "gray"}>
											{statusLabels[lead.status] || lead.status}
										</Badge>
									</div>
								</div>
								<DetailItem label="Priority" value={lead.priority ? lead.priority.charAt(0).toUpperCase() + lead.priority.slice(1) : "-"} />
								<DetailItem label="Next Follow-up" value={lead.next_follow_up_date ? formatDate(lead.next_follow_up_date) : "-"} />
								<DetailItem label="Remarks" value={lead.remarks || "-"} />
							</div>
						</CardContent>
					</Card>
				</div>

				{/* Notes */}
				<NotesSection leadId={leadId} notes={notesQuery.data || []} isLoading={notesQuery.isLoading} />

				{/* Activity History */}
				<Card>
					<CardHeader>
						<CardTitle className="text-lg">Activity History</CardTitle>
					</CardHeader>
					<CardContent>
						{activitiesQuery.isLoading ? (
							<div className="flex items-center justify-center h-32">
								<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
								<span className="ml-2 text-sm text-gray-600">Loading activities...</span>
							</div>
						) : activities.length === 0 ? (
							<div className="text-center py-8 text-gray-500">
								<IconRefresh className="w-12 h-12 mx-auto mb-2 text-gray-300" />
								<p>No activity recorded yet.</p>
							</div>
						) : (
							<div className="relative">
								<div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" />
								<div className="space-y-6">
									{activities.map((activity) => (
										<ActivityTimelineItem key={activity.id} activity={activity} />
									))}
								</div>
							</div>
						)}
					</CardContent>
				</Card>
			</div>

			{/* Edit Modal */}
			{isEditModalOpen && lead && (
				<EditLeadModal
					lead={lead}
					isOpen={isEditModalOpen}
					onClose={() => setIsEditModalOpen(false)}
					onSuccess={() => {
						leadQuery.refetch();
						activitiesQuery.refetch();
					}}
				/>
			)}
		</Content>
	);
}

function InfoCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
	return (
		<Card>
			<CardContent className="p-4">
				<div className="flex items-center gap-3">
					<div className="text-gray-500">{icon}</div>
					<div>
						<p className="text-xs text-gray-500">{label}</p>
						<p className="text-sm font-semibold text-gray-900">{value}</p>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

function DetailItem({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<p className="text-xs text-gray-500 font-medium">{label}</p>
			<p className="text-sm font-medium text-gray-900 mt-1">{value}</p>
		</div>
	);
}

const eventConfig: Record<string, { color: string; bgColor: string; icon: string }> = {
	created: { color: "text-green-600", bgColor: "bg-green-100", icon: "+" },
	updated: { color: "text-blue-600", bgColor: "bg-blue-100", icon: "~" },
	deleted: { color: "text-red-600", bgColor: "bg-red-100", icon: "-" },
};

const fieldLabels: Record<string, string> = {
	reference_number: "Reference Number",
	client_name: "Client Name",
	amount: "Amount",
	status: "Status",
	category: "Category",
	priority: "Priority",
	next_follow_up_date: "Next Follow-up Date",
};

function ActivityTimelineItem({ activity }: { activity: ActivityLog }) {
	const config = eventConfig[activity.event] || eventConfig.updated;
	const hasChanges = activity.properties.old || activity.properties.attributes;

	return (
		<div className="relative pl-10">
			<div className={`absolute left-2 top-1 w-5 h-5 rounded-full ${config.bgColor} flex items-center justify-center`}>
				<span className={`text-xs font-bold ${config.color}`}>{config.icon}</span>
			</div>

			<div className="bg-gray-50 rounded-lg p-3">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<span className="text-sm font-medium text-gray-900">{activity.description}</span>
					</div>
					<span className="text-xs text-gray-500">{formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}</span>
				</div>

				{activity.causer && <p className="text-xs text-gray-500 mt-1">by {activity.causer.name}</p>}

				{hasChanges && activity.event === "updated" && activity.properties.old && activity.properties.attributes && (
					<div className="mt-2 space-y-1">
						{Object.keys(activity.properties.attributes).map((key) => {
							const oldVal = activity.properties.old?.[key];
							const newVal = activity.properties.attributes?.[key];
							if (oldVal === newVal) return null;
							const label = fieldLabels[key] || key.replace(/_/g, " ");
							return (
								<div key={key} className="text-xs flex items-center gap-1">
									<span className="text-gray-500 capitalize">{label}:</span>
									<span className="text-red-500 line-through">{String(oldVal ?? "-")}</span>
									<span className="text-gray-400 mx-1">&rarr;</span>
									<span className="text-green-600 font-medium">{String(newVal ?? "-")}</span>
								</div>
							);
						})}
					</div>
				)}

				{activity.event === "created" && activity.properties.attributes && (
					<div className="mt-2 space-y-1">
						{Object.entries(activity.properties.attributes).map(([key, val]) => {
							const label = fieldLabels[key] || key.replace(/_/g, " ");
							return (
								<div key={key} className="text-xs flex items-center gap-1">
									<span className="text-gray-500 capitalize">{label}:</span>
									<span className="text-green-600 font-medium">{String(val ?? "-")}</span>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}

function NotesSection({ leadId, notes, isLoading }: { leadId: string; notes: LeadNote[]; isLoading: boolean }) {
	const [content, setContent] = useState("");
	const [files, setFiles] = useState<File[]>([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const createMutation = useLeadNoteCreateMutation(leadId);
	const deleteMutation = useLeadNoteDeleteMutation(leadId);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!content.trim()) return;

		setIsSubmitting(true);
		const formData = new FormData();
		formData.append("content", content);
		for (const file of files) {
			formData.append("files[]", file);
		}

		createMutation.mutate(formData, {
			onSuccess: () => {
				setContent("");
				setFiles([]);
				if (fileInputRef.current) fileInputRef.current.value = "";
				setIsSubmitting(false);
				toast.success("Note added");
			},
			onError: () => {
				setIsSubmitting(false);
				toast.error("Failed to add note");
			},
		});
	};

	const handleDelete = (noteId: string) => {
		deleteMutation.mutate(noteId, {
			onSuccess: () => toast.success("Note deleted"),
			onError: () => toast.error("Failed to delete note"),
		});
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) {
			setFiles(Array.from(e.target.files));
		}
	};

	const removeFile = (index: number) => {
		setFiles((prev) => prev.filter((_, i) => i !== index));
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-lg">Notes</CardTitle>
			</CardHeader>
			<CardContent>
				{/* Add Note Form */}
				<form onSubmit={handleSubmit} className="mb-6">
					<textarea
						className="w-full rounded-md border border-gray-300 p-3 text-sm focus:border-primary focus:ring-1 focus:ring-primary resize-none"
						rows={3}
						placeholder="Add a note..."
						value={content}
						onChange={(e) => setContent(e.target.value)}
						disabled={isSubmitting}
					/>
					<div className="flex items-center justify-between mt-2">
						<div className="flex items-center gap-2">
							<Button type="button" variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} disabled={isSubmitting}>
								<IconPaperclip size={16} />
								<span>Attach files</span>
							</Button>
							<input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileChange} />
							{files.length > 0 && <span className="text-xs text-gray-500">{files.length} file(s) selected</span>}
						</div>
						<Button type="submit" size="sm" disabled={isSubmitting || !content.trim()}>
							{isSubmitting ? "Posting..." : "Post"}
						</Button>
					</div>
					{files.length > 0 && (
						<div className="flex flex-wrap gap-2 mt-2">
							{files.map((file, i) => (
								<div key={file.name} className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1 text-xs">
									<IconFile size={14} className="text-gray-500" />
									<span className="max-w-[150px] truncate">{file.name}</span>
									<button type="button" onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-500">
										<IconX size={14} />
									</button>
								</div>
							))}
						</div>
					)}
				</form>

				{/* Notes List */}
				{isLoading ? (
					<div className="flex items-center justify-center h-24">
						<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
					</div>
				) : notes.length === 0 ? (
					<div className="text-center py-8 text-gray-500">
						<IconNote className="w-10 h-10 mx-auto mb-2 text-gray-300" />
						<p className="text-sm">No notes yet.</p>
					</div>
				) : (
					<div className="space-y-4">
						{notes.map((note) => (
							<div key={note.id} className="bg-gray-50 rounded-lg p-4">
								<div className="flex items-start justify-between">
									<div className="flex items-center gap-2">
										<div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
											<span className="text-xs font-semibold text-primary">{note.created_by?.name?.charAt(0) || "?"}</span>
										</div>
										<div>
											<span className="text-sm font-medium text-gray-900">{note.created_by?.name || "Unknown"}</span>
											<span className="text-xs text-gray-500 ml-2">{formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}</span>
										</div>
									</div>
									<button
										type="button"
										onClick={() => handleDelete(note.id)}
										className="text-gray-400 hover:text-red-500 transition-colors"
										title="Delete note"
									>
										<IconTrash size={16} />
									</button>
								</div>
								<p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{note.content}</p>
								{note.attachments.length > 0 && (
									<div className="flex flex-wrap gap-2 mt-3">
										{note.attachments.map((att) => (
											<a
												key={att.id}
												href={leadService.noteAttachmentDownloadUrl(note.id, att.id)}
												className="flex items-center gap-1.5 bg-white border border-gray-200 rounded px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-100 transition-colors"
												title={`${att.file_name} (${att.human_size})`}
											>
												<IconFile size={14} className="text-gray-400" />
												<span className="max-w-[120px] truncate">{att.file_name}</span>
												<IconDownload size={12} className="text-gray-400" />
											</a>
										))}
									</div>
								)}
							</div>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function EditLeadModal({ lead, isOpen, onClose, onSuccess }: { lead: Lead; isOpen: boolean; onClose: () => void; onSuccess: () => void }) {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const updateMutation = useLeadUpdateMutation(lead.id || "");

	const handleSubmit = async (values: CreateLeadData) => {
		setIsSubmitting(true);
		updateMutation.mutate(
			{ ...values, id: lead.id || "" },
			{
				onSuccess: () => {
					toast.success("Lead updated successfully");
					onSuccess();
					onClose();
					setIsSubmitting(false);
				},
				onError: () => {
					setIsSubmitting(false);
				},
			},
		);
	};

	return (
		<Dialog.Root open={isOpen} onOpenChange={onClose}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
				<Dialog.Content className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-4xl max-h-[90vh] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 overflow-y-auto rounded-lg">
					<div className="flex items-center justify-between">
						<Dialog.Title className="text-xl font-semibold">Edit Lead</Dialog.Title>
						<Dialog.Close asChild>
							<Button variant="ghost" size="sm" className="h-8 w-8 p-0">
								<IconX className="h-4 w-4" />
							</Button>
						</Dialog.Close>
					</div>
					<div className="py-4">
						<LeadForm lead={lead} onSubmit={handleSubmit} isSubmitting={isSubmitting} />
					</div>
					<div className="flex justify-end gap-2">
						<Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
							Cancel
						</Button>
						<Button
							type="submit"
							form="lead-form"
							disabled={isSubmitting}
							onClick={() => {
								const form = document.querySelector("#lead-form") as HTMLFormElement;
								if (form) form.requestSubmit();
							}}
						>
							{isSubmitting ? "Updating..." : "Update Lead"}
						</Button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
